import {
	Card,
	Button,
	Tag,
	Space,
	Typography,
	Row,
	Col,
	Descriptions,
	Divider,
	notification,
	Spin,
	Badge,
	Popconfirm,
	Modal,
	Form,
	Select,
	Table,
	Progress,
	List,
	Tabs,
	DatePicker,
	InputNumber,
	Input,
	Timeline,
	Checkbox,
} from "antd";
import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
	PiArrowLeftDuotone,
	PiCloudDuotone,
	PiHardDrivesDuotone,
	PiPackageDuotone,
	PiUserDuotone,
	PiReceiptDuotone,
	PiShoppingCartDuotone,
	PiInfoDuotone,
	PiStopCircleDuotone,
	PiTrashDuotone,
	PiCalendarDuotone,
	PiCurrencyDollarDuotone,
	PiPencilDuotone,
	PiClockDuotone,
	PiGlobeDuotone,
	PiDatabaseDuotone,
	PiChartBarDuotone,
	PiFileTextDuotone,
	PiAlignBottomDuotone,
	PiArrowDownDuotone,
	PiGearDuotone,
	PiSlidersDuotone,
	PiPlusDuotone,
	PiCloudArrowUpDuotone,
} from "react-icons/pi";
import {
	requestShowWEHOAccountDetails,
	requestSuspendInstance,
	requestTerminateInstance,
	requestUpdateInstanceStatus,
	requestUpdateInstanceBillingInformation,
	requestGetAPLAApplicationLogs,
	requestShowInstance,
	requestUpdateInstanceMetadata,
	requestStartInstanceProvisioning,
} from "../../services/admin.service";
import dayjs from "dayjs";
import { formatCurrency } from "../../utils/Formatter";
import {
	requestCreateAPLAApplicationEnvironment,
	requestDeleteAPLAApplicationEnvironment,
	requestGetAPLAApplicationDeployments,
	requestGetAPLAApplicationEnvironments,
	requestShowAPLAApplication,
	requestUpdateAPLAApplication,
	requestUpdateAPLAApplicationEnvironment,
} from "@/services/apla.admin.service";

const { Title, Text } = Typography;

function InstanceViewPage() {
	const navigate = useNavigate();
	const { id } = useParams();
	// get active tab from query
	const [searchParams, setSearchParams] = useSearchParams();
	const [loading, setLoading] = useState(false);
	const [instance, setInstance] = useState(null);
	const [suspendLoading, setSuspendLoading] = useState(false);
	const [terminateLoading, setTerminateLoading] = useState(false);
	const [updateStatusModalVisible, setUpdateStatusModalVisible] = useState(false);
	const [updateStatusLoading, setUpdateStatusLoading] = useState(false);
	const [statusForm] = Form.useForm();
	const [activeTabKey, setActiveTabKey] = useState(searchParams.get("tab") || "overview");
	const [aplaLogs, setAplaLogs] = useState(null);
	const aplaLogsRef = useRef(null);
	const [aplaEnvironments, setAplaEnvironments] = useState([]);
	const [aplaDeployments, setAplaDeployments] = useState([]);

	// Start Provisioning
	const [provisionLoading, setProvisionLoading] = useState(false);

	// Billing Update Modal
	const [updateBillingModalVisible, setUpdateBillingModalVisible] = useState(false);
	const [updateBillingLoading, setUpdateBillingLoading] = useState(false);
	const [billingForm] = Form.useForm();

	// General Info Update
	const [aplaApplication, setAplaApplication] = useState(null);
	const [updateGeneralInfoLoading, setUpdateGeneralInfoLoading] = useState(false);
	const [generalInfoForm] = Form.useForm();

	// Metadata Edit Modal
	const [editMetadataModalVisible, setEditMetadataModalVisible] = useState(false);
	const [editMetadataLoading, setEditMetadataLoading] = useState(false);
	const [metadataForm] = Form.useForm();

	// Create Environment Modal
	const [createEnvironmentModalVisible, setCreateEnvironmentModalVisible] = useState(false);
	const [createEnvironmentLoading, setCreateEnvironmentLoading] = useState(false);
	const [createEnvironmentForm] = Form.useForm();

	// WEHO Account
	const [wehoAccount, setWehoAccount] = useState(null);

	useEffect(() => {
		document.title = "Cloudiva Console | Instance Details";
		fetchInstanceDetails();
	}, [id]);

	useEffect(() => {
		if (
			activeTabKey == "logs" &&
			instance &&
			instance.type === "APLA" &&
			instance.metadata?.aplaApplicationId
		) {
			fetchAPLAApplicationLogs(instance.metadata.aplaApplicationId);
		}
	}, [instance, activeTabKey]);

	useEffect(() => {
		if (activeTabKey === "logs" && aplaLogs && aplaLogsRef.current) {
			aplaLogsRef.current?.scrollTo({ top: aplaLogsRef.current.scrollHeight, behavior: "smooth" });
		}
	}, [aplaLogs, activeTabKey, aplaLogsRef.current]);

	const fetchInstanceDetails = () => {
		setLoading(true);
		requestShowInstance(id)
			.then((response) => {
				const instanceData = response.data.data || response.data;
				setInstance(instanceData);

				if (instanceData.type === "WEHO" && instanceData.metadata?.wehoAccountId) {
					fetchInstanceWEHOAccount(instanceData.metadata.wehoAccountId);
				}

				if (instanceData.type === "APLA" && instanceData.metadata?.aplaApplicationId) {
					fetchAPLAApplicationDetails(instanceData.metadata.aplaApplicationId);
					fetchAPLAApplicationEnvironments(instanceData.metadata.aplaApplicationId);
					fetchAPLAApplicationDeployments(instanceData.metadata.aplaApplicationId);
				}
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch instance details",
				});
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const fetchInstanceWEHOAccount = (id) => {
		requestShowWEHOAccountDetails(id)
			.then((response) => {
				setWehoAccount(response.data.data || response.data);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch WEHO account details",
				});
			});
	};

	const fetchAPLAApplicationDetails = (id) => {
		requestShowAPLAApplication(id)
			.then((response) => {
				setAplaApplication(response.data.data || response.data);
				generalInfoForm.setFieldsValue({
					name: response.data.data.name,
					description: response.data.data.description,
				});
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch APLA application details",
				});
			});
	};

	const fetchAPLAApplicationDeployments = (id) => {
		requestGetAPLAApplicationDeployments(id)
			.then((response) => {
				setAplaDeployments(response.data.data);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch application deployments",
				});
			});
	};

	const fetchAPLAApplicationLogs = (id) => {
		requestGetAPLAApplicationLogs(id)
			.then((response) => {
				setAplaLogs(response.data.data);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: "Failed to fetch APLA application logs",
				});
			});
	};

	const fetchAPLAApplicationEnvironments = (id) => {
		requestGetAPLAApplicationEnvironments(id)
			.then((response) => {
				setAplaEnvironments(response.data.data);
			})
			.catch((error) => {
				console.error(error);
				notification.error({
					message: "Error",
					description:
						error.response?.data?.message || "Failed to fetch APLA application environments",
				});
			});
	};

	const handleSuspendInstance = () => {
		setSuspendLoading(true);
		requestSuspendInstance(id, { reason: "Suspended by admin" })
			.then(() => {
				notification.success({
					message: "Success",
					description: "Instance suspended successfully",
				});
				setInstance((prev) => ({
					...prev,
					status: "SUSPENDED",
				}));
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to suspend instance",
				});
			})
			.finally(() => {
				setSuspendLoading(false);
			});
	};

	const handleTerminateInstance = () => {
		setTerminateLoading(true);
		requestTerminateInstance(id, { reason: "Terminated by admin" })
			.then(() => {
				notification.success({
					message: "Success",
					description: "Instance terminated successfully",
				});
				setInstance((prev) => ({
					...prev,
					status: "TERMINATED",
				}));
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to terminate instance",
				});
			})
			.finally(() => {
				setTerminateLoading(false);
			});
	};

	const handleUpdateStatus = () => {
		setUpdateStatusModalVisible(true);
		statusForm.setFieldsValue({
			status: instance.status,
		});
	};

	const handleStartProvisioning = () => {
		setProvisionLoading(true);
		requestStartInstanceProvisioning(id)
			.then(() => {
				notification.success({
					message: "Success",
					description: "Instance provisioning started successfully",
				});
				// refetch instance details
				fetchInstanceDetails();
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to start instance provisioning",
				});
			})
			.finally(() => {
				setProvisionLoading(false);
			});
	};

	const handleUpdateStatusSubmit = (values) => {
		setUpdateStatusLoading(true);
		const payload = {
			status: values.status,
			reason: values.reason || "Status updated by admin",
		};

		requestUpdateInstanceStatus(id, payload)
			.then(() => {
				notification.success({
					message: "Success",
					description: "Instance status updated successfully",
				});
				setUpdateStatusModalVisible(false);
				statusForm.resetFields();
				setInstance((prev) => ({
					...prev,
					status: values.status,
				}));
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to update instance status",
				});
			})
			.finally(() => {
				setUpdateStatusLoading(false);
			});
	};

	const handleUpdateStatusCancel = () => {
		setUpdateStatusModalVisible(false);
		statusForm.resetFields();
	};

	const handleUpdateBilling = () => {
		setUpdateBillingModalVisible(true);
		billingForm.setFieldsValue({
			expiredAt: instance.expiredAt ? dayjs(instance.expiredAt) : null,
			nextBilledAt: instance.nextBilledAt ? dayjs(instance.nextBilledAt) : null,
			cycle: instance.cycle || null,
		});
	};

	const handleUpdateBillingSubmit = (values) => {
		setUpdateBillingLoading(true);
		const payload = {
			expiredAt: values.expiredAt ? values.expiredAt.toISOString() : null,
			nextBilledAt: values.nextBilledAt ? values.nextBilledAt.toISOString() : null,
			cycle: {
				amount: values.cycle.amount,
				interval: values.cycle.interval,
			},
		};

		requestUpdateInstanceBillingInformation(instance._id, payload)
			.then(() => {
				notification.success({
					message: "Success",
					description: "Instance billing information updated successfully",
				});
				setUpdateBillingModalVisible(false);
				billingForm.resetFields();
				// Update instance data with new billing info
				setInstance((prev) => ({
					...prev,
					expiredAt: payload.expiredAt,
					nextBilledAt: payload.nextBilledAt,
					cycle: payload.cycle,
				}));
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description:
						error.response?.data?.message || "Failed to update instance billing information",
				});
			})
			.finally(() => {
				setUpdateBillingLoading(false);
			});
	};

	const handleUpdateBillingCancel = () => {
		setUpdateBillingModalVisible(false);
		billingForm.resetFields();
	};

	// Metadata Edit Handlers
	const handleEditMetadata = () => {
		setEditMetadataModalVisible(true);
		metadataForm.setFieldsValue({
			metadata: JSON.stringify(instance.metadata, null, 2),
		});
	};

	const handleEditMetadataSubmit = (values) => {
		setEditMetadataLoading(true);

		try {
			// Validate JSON syntax
			const parsedMetadata = JSON.parse(values.metadata);

			requestUpdateInstanceMetadata(id, parsedMetadata)
				.then((response) => {
					if (response.data.code === 200) {
						notification.success({
							message: "Success",
							description: "Instance metadata updated successfully",
						});
						setEditMetadataModalVisible(false);
						metadataForm.resetFields();
						fetchInstanceDetails(); // Refresh the data
					} else {
						notification.error({
							message: "Error",
							description: response.data.message || "Failed to update instance metadata",
						});
					}
				})
				.catch((error) => {
					notification.error({
						message: "Error",
						description: error.response?.data?.message || "Failed to update instance metadata",
					});
				})
				.finally(() => {
					setEditMetadataLoading(false);
				});
		} catch (jsonError) {
			notification.error({
				message: "Invalid JSON",
				description: "Please enter valid JSON syntax",
			});
			setEditMetadataLoading(false);
		}
	};

	const handleEditMetadataCancel = () => {
		setEditMetadataModalVisible(false);
		metadataForm.resetFields();
	};

	const handleEnvironmentChange = (uuid, field, value) => {
		setAplaEnvironments((prevEnvs) =>
			prevEnvs.map((env) => (env.uuid === uuid ? { ...env, [field]: value } : env))
		);
	};

	const handleUpdateEnvironmentSubmit = () => {
		requestUpdateAPLAApplicationEnvironment(instance.metadata.aplaApplicationId, {
			environments: aplaEnvironments,
		})
			.then(() => {
				notification.success({
					message: "Success",
					description: "Application environments updated successfully",
				});
				// Refresh environments
				fetchAPLAApplicationEnvironments(instance.metadata.aplaApplicationId);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to update application environments",
				});
			});
	};

	const handleCreateEnvironment = () => {
		setCreateEnvironmentModalVisible(true);
		createEnvironmentForm.resetFields();
	};

	const handleCreateEnvironmentSubmit = (values) => {
		setCreateEnvironmentLoading(true);
		requestCreateAPLAApplicationEnvironment(instance.metadata.aplaApplicationId, {
			...values,
			isPreview: values.isPreview || false,
		})
			.then(() => {
				notification.success({
					message: "Success",
					description: "Environment variable created successfully",
				});
				setCreateEnvironmentModalVisible(false);
				createEnvironmentForm.resetFields();
				// Refresh environments
				fetchAPLAApplicationEnvironments(instance.metadata.aplaApplicationId);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to create environment variable",
				});
			})
			.finally(() => {
				setCreateEnvironmentLoading(false);
			});
	};

	const handleCreateEnvironmentCancel = () => {
		setCreateEnvironmentModalVisible(false);
		createEnvironmentForm.resetFields();
	};

	const handleDeleteEnvironmentSubmit = (envId) => {
		requestDeleteAPLAApplicationEnvironment(instance.metadata.aplaApplicationId, envId)
			.then(() => {
				notification.success({
					message: "Success",
					description: "Environment variable deleted successfully",
				});
				// Refresh environments
				fetchAPLAApplicationEnvironments(instance.metadata.aplaApplicationId);
			})
			.catch((error) => {
				notification.error({
					message: "Error",
					description: error.response?.data?.message || "Failed to delete environment variable",
				});
			});
	};

	const handleUpdateInstance = () => {
		generalInfoForm
			.validateFields()
			.then((values) => {
				setUpdateGeneralInfoLoading(true);
				console.log("Updating APLA Application with values:", values);
				requestUpdateAPLAApplication(instance.metadata.aplaApplicationId, {
					name: values.name,
					description: values.description,
				})
					.then((response) => {
						notification.success({
							message: "Success",
							description: "Application updated successfully",
						});
					})
					.catch((error) => {
						notification.error({
							message: "Error",
							description: error.response?.data?.message || "Failed to update application",
						});
					});
			})
			.catch((info) => {});
	};

	const getStatusColor = (status) => {
		switch (status) {
			case "PENDING":
				return "orange";
			case "PROVISIONING":
				return "processing";
			case "RUNNING":
			case "ACTIVE":
				return "green";
			case "SUSPENDED":
				return "red";
			case "CANCELLED":
				return "volcano";
			case "TERMINATED":
				return "default";
			default:
				return "blue";
		}
	};

	const getStatusText = (status) => {
		switch (status) {
			case "PENDING":
				return "Pending";
			case "PROVISIONING":
				return "Provisioning";
			case "RUNNING":
				return "Running";
			case "ACTIVE":
				return "Active";
			case "SUSPENDED":
				return "Suspended";
			case "CANCELLED":
				return "Cancelled";
			case "TERMINATED":
				return "Terminated";
			default:
				return status;
		}
	};

	if (loading) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", padding: "50px 0" }}>
					<Spin size="large" />
				</div>
			</div>
		);
	}

	if (!instance) {
		return (
			<div className="page-content">
				<div style={{ textAlign: "center", padding: "50px 0" }}>
					<Text>Instance not found</Text>
				</div>
			</div>
		);
	}

	return (
		<div className="page-content">
			<div style={{ marginBottom: 24 }}>
				<Space split={<Divider type="vertical" />}>
					<Space>
						<Button icon={<PiArrowLeftDuotone />} onClick={() => navigate("/instances")} />
						<Title level={4} style={{ margin: 0 }}>
							Instance Details
						</Title>
						<Tag color={getStatusColor(instance.status)}>{getStatusText(instance.status)}</Tag>
					</Space>
				</Space>
			</div>

			<div className="page-content-wrapper">
				<Row gutter={24}>
					{/* Main Content */}
					<Col span={24}>
						{/* Tabs for organized content */}
						<Tabs
							defaultActiveKey={activeTabKey}
							type="line"
							size="middle"
							style={{ marginBottom: 24 }}
							onChange={(activeKey) => {
								setActiveTabKey(activeKey);
								setSearchParams({ tab: activeKey });
							}}
							items={[
								{
									key: "overview",
									label: (
										<Space>
											<PiInfoDuotone />
											Overview
										</Space>
									),
									children: (
										<Row gutter={24}>
											<Col span={16}>
												<Card
													type="inner"
													title={"Details"}
													extra={
														<Space>
															{instance && instance.status === "PENDING" && (
																<Button
																	loading={provisionLoading}
																	type="primary"
																	icon={<PiGearDuotone />}
																	onClick={handleStartProvisioning}>
																	Start Provisioning
																</Button>
															)}
															<Button
																type="default"
																icon={<PiPencilDuotone />}
																onClick={handleUpdateStatus}>
																Update Status
															</Button>
															<Button
																type="default"
																icon={<PiReceiptDuotone />}
																onClick={handleUpdateBilling}>
																Update Billing
															</Button>
															{instance.status === "ACTIVE" && (
																<Popconfirm
																	title="Suspend Instance"
																	description="Are you sure you want to suspend this instance?"
																	onConfirm={handleSuspendInstance}
																	okText="Yes, Suspend"
																	cancelText="Cancel">
																	<Button
																		type="default"
																		danger
																		icon={<PiStopCircleDuotone />}
																		loading={suspendLoading}>
																		Suspend
																	</Button>
																</Popconfirm>
															)}
															{(instance.status === "ACTIVE" ||
																instance.status === "SUSPENDED") && (
																<Popconfirm
																	title="Terminate Instance"
																	description="This action cannot be undone. Are you sure you want to terminate this instance?"
																	onConfirm={handleTerminateInstance}
																	okText="Yes, Terminate"
																	cancelText="Cancel">
																	<Button
																		type="primary"
																		danger
																		icon={<PiTrashDuotone />}
																		loading={terminateLoading}>
																		Terminate
																	</Button>
																</Popconfirm>
															)}
														</Space>
													}
													style={{ marginBottom: 24 }}>
													<Descriptions column={2} bordered size="small">
														<Descriptions.Item label="Instance Number" span={1}>
															<Text strong copyable>
																{instance.number}
															</Text>
														</Descriptions.Item>
														<Descriptions.Item label="Status" span={1}>
															<Tag color={getStatusColor(instance.status)}>
																{getStatusText(instance.status)}
															</Tag>
														</Descriptions.Item>
														<Descriptions.Item label="Type" span={1}>
															<Tag color="blue">{instance.type}</Tag>
														</Descriptions.Item>
														<Descriptions.Item label="Instance ID" span={1}>
															<Text code copyable>
																{instance._id}
															</Text>
														</Descriptions.Item>
														<Descriptions.Item label="Created Date" span={1}>
															<Space>
																<PiCalendarDuotone />
																{dayjs(instance.createdAt).format("DD MMMM YYYY, HH:mm")}
															</Space>
														</Descriptions.Item>
														<Descriptions.Item label="Last Updated" span={1}>
															<Space>
																<PiCalendarDuotone />
																{dayjs(instance.updatedAt).format("DD MMMM YYYY, HH:mm")}
															</Space>
														</Descriptions.Item>
														{instance.expiredAt && (
															<Descriptions.Item label="Expired Date" span={1}>
																<Space>
																	<PiCalendarDuotone />
																	{dayjs(instance.expiredAt).format("DD MMMM YYYY, HH:mm")}
																</Space>
															</Descriptions.Item>
														)}
														{instance.nextBilledAt && (
															<Descriptions.Item label="Next Billing" span={1}>
																<Space>
																	<PiCalendarDuotone />
																	{dayjs(instance.nextBilledAt).format("DD MMMM YYYY, HH:mm")}
																</Space>
															</Descriptions.Item>
														)}
														{instance.cycle && (
															<Descriptions.Item label="Billing Cycle" span={2}>
																<Space>
																	<PiClockDuotone />
																	<Text strong>
																		{instance.cycle.amount} {instance.cycle.interval}
																	</Text>
																</Space>
															</Descriptions.Item>
														)}
														{instance.type === "WEHO" && (
															<Descriptions.Item label="Domain" span={2}>
																<Space>
																	<PiGlobeDuotone />
																	<Text
																		editable={{
																			onChange: (value) => {
																				// show confirmation
																				Modal.confirm({
																					title: "Confirm Domain Change",
																					content: `Are you sure you want to change the domain to ${value}?`,
																					onOk: () => {
																						setEditMetadataLoading(true);

																						requestUpdateInstanceMetadata(id, {
																							domain: value,
																						})
																							.then((response) => {
																								if (response.data.code === 200) {
																									notification.success({
																										message: "Success",
																										description:
																											"Instance metadata updated successfully",
																									});
																									setEditMetadataModalVisible(false);
																									metadataForm.resetFields();
																									fetchInstanceDetails(); // Refresh the data
																								} else {
																									notification.error({
																										message: "Error",
																										description:
																											response.data.message ||
																											"Failed to update instance metadata",
																									});
																								}
																							})
																							.catch((error) => {
																								notification.error({
																									message: "Error",
																									description:
																										error.response?.data?.message ||
																										"Failed to update instance metadata",
																								});
																							})
																							.finally(() => {
																								setEditMetadataLoading(false);
																							});
																					},
																				});
																			},
																		}}
																		strong>
																		{instance.metadata?.domain}
																	</Text>
																</Space>
															</Descriptions.Item>
														)}
													</Descriptions>
												</Card>
												<Row gutter={24}>
													<Col span={12}>
														<Card type="inner" title={"Service"}>
															<div style={{ marginBottom: 16 }}>
																<Text strong style={{ display: "block", marginBottom: 8 }}>
																	{instance.service?.name}
																</Text>
																<div style={{ marginBottom: 8 }}>
																	<Text type="secondary">Code: </Text>
																	<Tag>{instance.service?.code}</Tag>
																</div>
																<div style={{ marginBottom: 8 }}>
																	<Text type="secondary">Status: </Text>
																	<Tag color="green">{instance.service?.status}</Tag>
																</div>
																<div>
																	<Text type="secondary">Description: </Text>
																	<Text>{instance.service?.description || "No description"}</Text>
																</div>
															</div>
															{instance.service?.features &&
																instance.service.features.length > 0 && (
																	<div>
																		<Text strong style={{ display: "block", marginBottom: 8 }}>
																			Features:
																		</Text>
																		<Space wrap>
																			{instance.service.features.map((feature) => (
																				<Tag key={feature._id} color="blue">
																					{feature.name}
																				</Tag>
																			))}
																		</Space>
																	</div>
																)}
														</Card>
													</Col>
													<Col span={12}>
														<Card type="inner" title={"Plan"}>
															<div style={{ marginBottom: 16 }}>
																<Text strong style={{ display: "block", marginBottom: 8 }}>
																	{instance.plan?.name}
																</Text>
																<div style={{ marginBottom: 8 }}>
																	<Text type="secondary">Code: </Text>
																	<Tag>{instance.plan?.code}</Tag>
																</div>
																<div style={{ marginBottom: 8 }}>
																	<Text type="secondary">Status: </Text>
																	<Tag color="green">{instance.plan?.status}</Tag>
																</div>
																<div>
																	<Text type="secondary">Description: </Text>
																	<Text>{instance.plan?.description || "No description"}</Text>
																</div>
															</div>
															{instance.plan?.price && (
																<div>
																	<Text strong style={{ display: "block", marginBottom: 8 }}>
																		Pricing:
																	</Text>
																	<div
																		style={{ fontSize: "16px", fontWeight: 600, color: "#1890ff" }}>
																		{formatCurrency(instance.plan.price.amount)} /{" "}
																		{instance.plan.price.unit} / {instance.plan.price.interval}
																	</div>
																</div>
															)}
														</Card>
													</Col>
												</Row>
											</Col>
											<Col span={8}>
												<Card type="inner" title={"Timestamps"} style={{ marginBottom: 24 }}>
													<div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
														<div style={{ display: "flex", justifyContent: "space-between" }}>
															<Text type="secondary">Created:</Text>
															<Text>{dayjs(instance.createdAt).format("DD MMM YYYY, HH:mm")}</Text>
														</div>
														<div style={{ display: "flex", justifyContent: "space-between" }}>
															<Text type="secondary">Updated:</Text>
															<Text>{dayjs(instance.updatedAt).format("DD MMM YYYY, HH:mm")}</Text>
														</div>
														{instance.expiredAt && (
															<div style={{ display: "flex", justifyContent: "space-between" }}>
																<Text type="secondary">Expires:</Text>
																<Text
																	style={{
																		color: dayjs(instance.expiredAt).isBefore(dayjs())
																			? "#ff4d4f"
																			: "inherit",
																	}}>
																	{dayjs(instance.expiredAt).format("DD MMM YYYY, HH:mm")}
																</Text>
															</div>
														)}
														{instance.nextBilledAt && (
															<div style={{ display: "flex", justifyContent: "space-between" }}>
																<Text type="secondary">Next Billing:</Text>
																<Text>
																	{dayjs(instance.nextBilledAt).format("DD MMM YYYY, HH:mm")}
																</Text>
															</div>
														)}
														<div style={{ display: "flex", justifyContent: "space-between" }}>
															<Text type="secondary">Age:</Text>
															<Text>{dayjs(instance.createdAt).fromNow()}</Text>
														</div>
													</div>
												</Card>
												<Row gutter={16}>
													{instance.orderId && (
														<Col span={8}>
															<Card type="inner" size="small">
																<div style={{ textAlign: "center" }}>
																	<PiShoppingCartDuotone
																		size={24}
																		style={{ color: "#1890ff", marginBottom: 8 }}
																	/>
																	<div style={{ marginBottom: 8 }}>
																		<Text strong>Related Order</Text>
																	</div>
																	<Button
																		type="primary"
																		onClick={() => navigate(`/orders/${instance.orderId}`)}>
																		View Order
																	</Button>
																</div>
															</Card>
														</Col>
													)}
													{instance.invoiceId && (
														<Col span={8}>
															<Card type="inner" size="small">
																<div style={{ textAlign: "center" }}>
																	<PiReceiptDuotone
																		size={24}
																		style={{ color: "#52c41a", marginBottom: 8 }}
																	/>
																	<div style={{ marginBottom: 8 }}>
																		<Text strong>Related Invoice</Text>
																	</div>
																	<Button
																		type="primary"
																		onClick={() => navigate(`/invoices/${instance.invoiceId}`)}>
																		View Invoice
																	</Button>
																</div>
															</Card>
														</Col>
													)}
													{instance.teamId && (
														<Col span={8}>
															<Card type="inner" size="small">
																<div style={{ textAlign: "center" }}>
																	<PiUserDuotone
																		size={24}
																		style={{ color: "#722ed1", marginBottom: 8 }}
																	/>
																	<div style={{ marginBottom: 8 }}>
																		<Text strong>Team</Text>
																	</div>
																	<Button
																		type="primary"
																		onClick={() => navigate(`/teams/${instance.teamId}`)}>
																		View Team
																	</Button>
																</div>
															</Card>
														</Col>
													)}
												</Row>
											</Col>
										</Row>
									),
								},
								instance &&
									instance.type === "WEHO" && {
										key: "account",
										label: (
											<Space>
												<PiUserDuotone />
												Account
											</Space>
										),
										children:
											wehoAccount &&
											(wehoAccount.metadata?.account_info || wehoAccount.metadata?.resource) ? (
												<Row gutter={24}>
													<Col span={12}>
														<Card title={"Details"} type="inner" style={{ marginBottom: 24 }}>
															<Descriptions size="small" column={1} bordered>
																{wehoAccount.metadata?.user && (
																	<Descriptions.Item label="Username">
																		<Text code>{wehoAccount.metadata.user}</Text>
																	</Descriptions.Item>
																)}
																{wehoAccount.metadata?.domain && (
																	<Descriptions.Item label="Domain">
																		<Text code copyable>
																			{wehoAccount.metadata.domain}
																		</Text>
																	</Descriptions.Item>
																)}
																{wehoAccount.metadata?.email && (
																	<Descriptions.Item label="Email">
																		<Text>{wehoAccount.metadata.email}</Text>
																	</Descriptions.Item>
																)}
																{wehoAccount.metadata?.homedir && (
																	<Descriptions.Item label="Home Directory">
																		<Text code>{wehoAccount.metadata.homedir}</Text>
																	</Descriptions.Item>
																)}
																{wehoAccount.metadata?.plan && (
																	<Descriptions.Item label="Plan">
																		<Text
																			style={{
																				textTransform: "uppercase",
																			}}>
																			{wehoAccount.metadata.plan}
																		</Text>
																	</Descriptions.Item>
																)}
																{wehoAccount.metadata?.created && (
																	<Descriptions.Item label="Created">
																		<Text>
																			{dayjs
																				.unix(wehoAccount.metadata.created)
																				.format("DD MMMM YYYY, HH:mm")}
																		</Text>
																	</Descriptions.Item>
																)}
																{wehoAccount.metadata?.ip && (
																	<Descriptions.Item label="IP Address">
																		<Text code>{wehoAccount.metadata.ip}</Text>
																	</Descriptions.Item>
																)}
																{/* CWP Format Details */}
																{wehoAccount.metadata?.account_info?.directory && (
																	<Descriptions.Item label="Directory">
																		<Text code>{wehoAccount.metadata.account_info.directory}</Text>
																	</Descriptions.Item>
																)}
																{wehoAccount.metadata?.account_info?.email && (
																	<Descriptions.Item label="Email">
																		<Text>{wehoAccount.metadata.account_info.email}</Text>
																	</Descriptions.Item>
																)}
																{wehoAccount.metadata?.account_info?.package_name && (
																	<Descriptions.Item label="Package Name">
																		<Text>{wehoAccount.metadata.account_info.package_name}</Text>
																	</Descriptions.Item>
																)}
																{wehoAccount.metadata?.account_info?.state && (
																	<Descriptions.Item label="Account State">
																		<Tag
																			color={
																				wehoAccount.metadata.account_info.state === "active"
																					? "green"
																					: "red"
																			}>
																			{wehoAccount.metadata.account_info.state}
																		</Tag>
																	</Descriptions.Item>
																)}
															</Descriptions>
															<div
																style={{
																	marginTop: 16,
																	width: "100%",
																	display: "flex",
																	justifyContent: "flex-end",
																}}>
																<Button
																	style={{ width: "100%" }}
																	type="primary"
																	onClick={() => {
																		window.open(
																			`https://${wehoAccount.server.hostname}:2003`,
																			"_blank"
																		);
																	}}>
																	Login ke Panel
																</Button>
															</div>
														</Card>
														<Card title={"Usage"} type="inner">
															{/* Storage Usage - Handle both CWP and WEBUZO */}
															<div style={{ marginBottom: 24 }}>
																<div
																	style={{
																		display: "flex",
																		justifyContent: "space-between",
																		marginBottom: 8,
																	}}>
																	<Text strong>Disk Space</Text>
																	<Text type="secondary">
																		{wehoAccount.metadata?.resource?.disk ? (
																			// WEBUZO format
																			<>
																				{wehoAccount.metadata.resource.disk.used} /{" "}
																				{wehoAccount.metadata.resource.disk.limit === "0B"
																					? "∞"
																					: wehoAccount.metadata.resource.disk.limit}
																			</>
																		) : (
																			// CWP format
																			<>
																				{wehoAccount.metadata?.account_info?.space_usage?.toFixed(
																					2
																				) || 0}{" "}
																				MB /{" "}
																				{wehoAccount.metadata?.account_info?.space_disk ===
																				"unlimited"
																					? "∞"
																					: `${wehoAccount.metadata?.account_info?.space_disk} MB`}
																			</>
																		)}
																	</Text>
																</div>
																<Progress
																	percent={
																		wehoAccount.metadata?.resource?.disk
																			? // WEBUZO format
																			  wehoAccount.metadata.resource.disk.percent
																			: // CWP format
																			wehoAccount.metadata?.account_info?.space_disk === "unlimited"
																			? 0
																			: ((wehoAccount.metadata?.account_info?.space_usage || 0) /
																					parseFloat(
																						wehoAccount.metadata?.account_info?.space_disk
																					)) *
																			  100
																	}
																	showInfo={false}
																	status={
																		wehoAccount.metadata?.resource?.disk
																			? // WEBUZO format
																			  wehoAccount.metadata.resource.disk.percent > 80
																				? "exception"
																				: "normal"
																			: // CWP format
																			wehoAccount.metadata?.account_info?.space_disk === "unlimited"
																			? "normal"
																			: ((wehoAccount.metadata?.account_info?.space_usage || 0) /
																					parseFloat(
																						wehoAccount.metadata?.account_info?.space_disk
																					)) *
																					100 >
																			  80
																			? "exception"
																			: "normal"
																	}
																	strokeColor="#1890ff"
																/>
															</div>

															{/* Bandwidth Usage - Handle both CWP and WEBUZO */}
															<div style={{ marginBottom: 24 }}>
																<div
																	style={{
																		display: "flex",
																		justifyContent: "space-between",
																		marginBottom: 8,
																	}}>
																	<Text strong>Bandwidth</Text>
																	<Text type="secondary">
																		{wehoAccount.metadata?.resource?.bandwidth ? (
																			// WEBUZO format
																			<>
																				{wehoAccount.metadata.resource.bandwidth.used} /{" "}
																				{wehoAccount.metadata.resource.bandwidth.limit ===
																				"unlimited"
																					? "∞"
																					: wehoAccount.metadata.resource.bandwidth.limit}
																			</>
																		) : (
																			// CWP format
																			<>
																				{wehoAccount.metadata?.account_info?.bandwidth_used || 0} MB
																				/ {wehoAccount.metadata?.account_info?.bandwidth || 0} MB
																			</>
																		)}
																	</Text>
																</div>
																<Progress
																	percent={
																		wehoAccount.metadata?.resource?.bandwidth
																			? // WEBUZO format
																			  wehoAccount.metadata.resource.bandwidth.percent
																			: // CWP format
																			parseFloat(wehoAccount.metadata?.account_info?.bandwidth) > 0
																			? ((parseFloat(
																					wehoAccount.metadata?.account_info?.bandwidth_used
																			  ) || 0) /
																					parseFloat(
																						wehoAccount.metadata?.account_info?.bandwidth
																					)) *
																			  100
																			: 0
																	}
																	showInfo={false}
																	status={
																		wehoAccount.metadata?.resource?.bandwidth
																			? // WEBUZO format
																			  wehoAccount.metadata.resource.bandwidth.percent > 80
																				? "exception"
																				: "normal"
																			: // CWP format
																			parseFloat(wehoAccount.metadata?.account_info?.bandwidth) >
																					0 &&
																			  ((parseFloat(
																					wehoAccount.metadata?.account_info?.bandwidth_used
																			  ) || 0) /
																					parseFloat(
																						wehoAccount.metadata?.account_info?.bandwidth
																					)) *
																					100 >
																					80
																			? "exception"
																			: "normal"
																	}
																	strokeColor="#52c41a"
																/>
															</div>
															<Divider />
															{/* Account Statistics - Handle both CWP and WEBUZO */}
															<Row gutter={16}>
																<Col span={8}>
																	<Space direction="vertical">
																		<Text strong>FTP Accounts</Text>
																		<Progress
																			style={{ marginTop: 8 }}
																			type="circle"
																			percent={
																				wehoAccount.metadata?.resource?.ftp
																					? // WEBUZO format
																					  wehoAccount.metadata.resource.ftp.percent
																					: // CWP format
																					wehoAccount.metadata?.account_info?.ftp_accounts_used
																					? (wehoAccount.metadata.account_info.ftp_accounts_used /
																							wehoAccount.metadata.account_info.ftp_accounts) *
																					  100
																					: 0
																			}
																			format={(percent) =>
																				wehoAccount.metadata?.resource?.ftp
																					? // WEBUZO format
																					  `${wehoAccount.metadata.resource.ftp.used} / ${
																							wehoAccount.metadata.resource.ftp.limit ===
																							"unlimited"
																								? "∞"
																								: wehoAccount.metadata.resource.ftp.limit
																					  }`
																					: // CWP format
																					  `${
																							wehoAccount.metadata?.account_info
																								?.ftp_accounts_used || 0
																					  } / ${
																							wehoAccount.metadata?.account_info?.ftp_accounts || 0
																					  }`
																			}
																		/>
																	</Space>
																</Col>
																<Col span={8}>
																	<Space direction="vertical">
																		<Text strong>Email Accounts</Text>
																		<Progress
																			type="circle"
																			percent={
																				wehoAccount.metadata?.resource?.email_account
																					? // WEBUZO format
																					  wehoAccount.metadata.resource.email_account.percent
																					: // CWP format
																					wehoAccount.metadata?.account_info?.email_accounts_used
																					? (wehoAccount.metadata.account_info.email_accounts_used /
																							wehoAccount.metadata.account_info.email_accounts) *
																					  100
																					: 0
																			}
																			format={(percent) =>
																				wehoAccount.metadata?.resource?.email_account
																					? // WEBUZO format
																					  `${
																							wehoAccount.metadata.resource.email_account.used
																					  } / ${
																							wehoAccount.metadata.resource.email_account.limit ===
																							"unlimited"
																								? "∞"
																								: wehoAccount.metadata.resource.email_account.limit
																					  }`
																					: // CWP format
																					  `${
																							wehoAccount.metadata?.account_info
																								?.email_accounts_used || 0
																					  } / ${
																							wehoAccount.metadata?.account_info?.email_accounts ||
																							0
																					  }`
																			}
																		/>
																	</Space>
																</Col>
																<Col span={8}>
																	<Space direction="vertical">
																		<Text strong>Databases</Text>
																		<Progress
																			type="circle"
																			percent={
																				wehoAccount.metadata?.resource?.db
																					? // WEBUZO format
																					  wehoAccount.metadata.resource.db.percent
																					: // CWP format
																					wehoAccount.metadata?.account_info?.db_used
																					? (wehoAccount.metadata.account_info.db_used /
																							wehoAccount.metadata.account_info.db_max) *
																					  100
																					: 0
																			}
																			format={(percent) =>
																				wehoAccount.metadata?.resource?.db
																					? // WEBUZO format
																					  `${wehoAccount.metadata.resource.db.used} / ${
																							wehoAccount.metadata.resource.db.limit === "unlimited"
																								? "∞"
																								: wehoAccount.metadata.resource.db.limit
																					  }`
																					: // CWP format
																					  `${
																							wehoAccount.metadata?.account_info?.db_used || 0
																					  } / ${wehoAccount.metadata?.account_info?.db_max || 0}`
																			}
																		/>
																	</Space>
																</Col>
															</Row>
															<Divider />
															<Row gutter={16}>
																<Col span={8}>
																	<Space direction="vertical">
																		<Text strong>Addon Domains</Text>
																		<Progress
																			type="circle"
																			percent={
																				wehoAccount.metadata?.resource?.addondom
																					? // WEBUZO format
																					  wehoAccount.metadata.resource.addondom.percent
																					: // CWP format
																					wehoAccount.metadata?.account_info?.addons_domains_used
																					? (wehoAccount.metadata.account_info.addons_domains_used /
																							wehoAccount.metadata.account_info.addons_domains) *
																					  100
																					: 0
																			}
																			format={(percent) =>
																				wehoAccount.metadata?.resource?.addondom
																					? // WEBUZO format
																					  `${wehoAccount.metadata.resource.addondom.used} / ${
																							wehoAccount.metadata.resource.addondom.limit ===
																							"unlimited"
																								? "∞"
																								: wehoAccount.metadata.resource.addondom.limit
																					  }`
																					: // CWP format
																					  `${
																							wehoAccount.metadata?.account_info
																								?.addons_domains_used || 0
																					  } / ${
																							wehoAccount.metadata?.account_info?.addons_domains ||
																							0
																					  }`
																			}
																		/>
																	</Space>
																</Col>
																<Col span={8}>
																	<Space direction="vertical">
																		<Text strong>Sub Domains</Text>
																		<Progress
																			type="circle"
																			percent={
																				wehoAccount.metadata?.resource?.subdom
																					? // WEBUZO format
																					  wehoAccount.metadata.resource.subdom.percent
																					: // CWP format
																					wehoAccount.metadata?.account_info?.sub_domains_used
																					? (wehoAccount.metadata.account_info.sub_domains_used /
																							wehoAccount.metadata.account_info.sub_domains) *
																					  100
																					: 0
																			}
																			format={(percent) =>
																				wehoAccount.metadata?.resource?.subdom
																					? // WEBUZO format
																					  `${wehoAccount.metadata.resource.subdom.used} / ${
																							wehoAccount.metadata.resource.subdom.limit ===
																							"unlimited"
																								? "∞"
																								: wehoAccount.metadata.resource.subdom.limit
																					  }`
																					: // CWP format
																					  `${
																							wehoAccount.metadata?.account_info
																								?.sub_domains_used || 0
																					  } / ${
																							wehoAccount.metadata?.account_info?.sub_domains || 0
																					  }`
																			}
																		/>
																	</Space>
																</Col>
															</Row>
														</Card>
													</Col>
													<Col span={12}>
														<Card title={"Domains"} type="inner" style={{ marginBottom: 24 }}>
															{wehoAccount.metadata?.domains &&
															wehoAccount.metadata.domains.length > 0 ? (
																<List
																	size="small"
																	dataSource={wehoAccount.metadata.domains}
																	renderItem={(domain) => (
																		<List.Item>
																			<List.Item.Meta
																				avatar={
																					<PiGlobeDuotone size={20} style={{ color: "#1890ff" }} />
																				}
																				title={domain.domain}
																				description={
																					<div>
																						<div>
																							<Text type="secondary">Path:</Text>{" "}
																							<Text code>{domain.path}</Text>
																						</div>
																						<div>
																							<Text type="secondary">Email:</Text>{" "}
																							<Text>{domain.email}</Text>
																						</div>
																					</div>
																				}
																			/>
																		</List.Item>
																	)}
																/>
															) : (
																<div style={{ textAlign: "center", padding: "20px 0" }}>
																	<Text type="secondary">No domains configured</Text>
																</div>
															)}
														</Card>
														<Card title={"Databases"} type="inner">
															{wehoAccount.metadata?.databases &&
															wehoAccount.metadata.databases.length > 0 ? (
																<List
																	size="small"
																	dataSource={wehoAccount.metadata.databases}
																	renderItem={(database) => (
																		<List.Item>
																			<List.Item.Meta
																				avatar={
																					<PiDatabaseDuotone
																						size={20}
																						style={{ color: "#52c41a" }}
																					/>
																				}
																				title={database.database}
																				description={
																					<div>
																						{database.user && (
																							<div>
																								<Text type="secondary">User:</Text>{" "}
																								<Text>{database.user}</Text>
																							</div>
																						)}
																						{database.host && (
																							<div>
																								<Text type="secondary">Host:</Text>{" "}
																								<Text>{database.host}</Text>
																							</div>
																						)}
																					</div>
																				}
																			/>
																		</List.Item>
																	)}
																/>
															) : (
																<div style={{ textAlign: "center", padding: "20px 0" }}>
																	<Text type="secondary">No databases configured</Text>
																</div>
															)}
														</Card>
													</Col>
												</Row>
											) : (
												<Card>
													<Spin />
												</Card>
											),
									},
								instance &&
									instance.type === "APLA" && {
										key: "deployments",
										label: (
											<Space>
												<PiCloudArrowUpDuotone />
												Deployments
												{aplaDeployments && aplaDeployments.length > 0 && (
													<Badge count={aplaDeployments.length} />
												)}
											</Space>
										),
										children: (
											<Card
												title="Application Deployments"
												type="inner"
												style={{ marginBottom: 24 }}>
												{aplaDeployments && aplaDeployments.length > 0 ? (
													<Table
														dataSource={aplaDeployments}
														rowKey="id"
														pagination={false}
														columns={[
															{
																title: "ID",
																dataIndex: "id",
																key: "id",
																width: 70,
																render: (text) => <Text code>{text}</Text>,
															},
															{
																title: "Status",
																dataIndex: "status",
																key: "status",
																width: 100,
																render: (status) => {
																	const getDeploymentStatusColor = (status) => {
																		switch (status?.toLowerCase()) {
																			case "finished":
																			case "success":
																				return "green";
																			case "running":
																			case "in_progress":
																				return "processing";
																			case "failed":
																			case "error":
																				return "red";
																			case "cancelled":
																				return "default";
																			case "queued":
																			case "pending":
																				return "orange";
																			default:
																				return "blue";
																		}
																	};
																	return (
																		<Tag color={getDeploymentStatusColor(status)}>
																			{status?.toUpperCase()}
																		</Tag>
																	);
																},
															},
															{
																title: "Commit",
																dataIndex: "commit",
																key: "commit",
																width: 120,
																render: (commit) => (
																	<Text code copyable={{ text: commit }}>
																		{commit?.substring(0, 7)}
																	</Text>
																),
															},
															{
																title: "Message",
																dataIndex: "commit_message",
																key: "commit_message",
																render: (message) => (
																	<Text ellipsis={{ tooltip: message }}>
																		{message || "No message"}
																	</Text>
																),
															},
															{
																title: "Started",
																dataIndex: "created_at",
																key: "created_at",
																width: 180,
																render: (date) => (
																	<Text>{dayjs(date).format("DD MMM, HH:mm:ss")}</Text>
																),
															},
															{
																title: "Finished",
																dataIndex: "finished_at",
																key: "finished_at",
																width: 180,
																render: (date) =>
																	date ? (
																		<Text>
																			{dayjs(`${date}.000000Z`).format("DD MMM, HH:mm:ss")}
																		</Text>
																	) : (
																		<Text type="secondary">-</Text>
																	),
															},
															{
																title: "Duration",
																key: "duration",
																width: 100,
																render: (_, record) => {
																	if (record.finished_at && record.created_at) {
																		const duration = dayjs(`${record.finished_at}.000000Z`).diff(
																			dayjs(record.created_at),
																			"seconds"
																		);
																		const minutes = Math.floor(duration / 60);
																		const seconds = duration % 60;
																		return (
																			<Text>
																				{minutes > 0 && `${minutes}m `}
																				{seconds}s
																			</Text>
																		);
																	}
																	return <Text type="secondary">-</Text>;
																},
															},
															{
																title: "",
																key: "actions",
																render: (_, record) => (
																	<Space>
																		{record.logs && (
																			<Button
																				type="link"
																				size="small"
																				icon={<PiFileTextDuotone />}
																				onClick={() => {
																					Modal.info({
																						title: `Deployment Logs - #${record.id}`,
																						width: 1000,
																						content: (
																							<div style={{ maxHeight: "60vh", overflow: "auto" }}>
																								{(() => {
																									try {
																										const logs = JSON.parse(record.logs);
																										return (
																											<Timeline
																												mode="left"
																												items={logs.map((log, index) => ({
																													key: index,
																													color:
																														log.type === "stderr" ? "red" : "blue",
																													children: (
																														<div>
																															{log.command && (
																																<div style={{ marginBottom: 4 }}>
																																	<Text
																																		code
																																		style={{
																																			fontSize: "11px",
																																			backgroundColor: "#f0f2f5",
																																		}}>
																																		$ {log.command}
																																	</Text>
																																</div>
																															)}
																															<div style={{ marginBottom: 4 }}>
																																<Text
																																	style={{
																																		fontSize: "12px",
																																		fontFamily: "monospace",
																																		whiteSpace: "pre-wrap",
																																		// color:
																																		// 	log.type === "stderr"
																																		// 		? "#ff4d4f"
																																		// 		: "inherit",
																																	}}>
																																	{log.output}
																																</Text>
																															</div>
																															<div>
																																<Text
																																	type="secondary"
																																	style={{ fontSize: "10px" }}>
																																	{dayjs(log.timestamp).format(
																																		"HH:mm:ss.SSS"
																																	)}{" "}
																																	- {log.type}
																																</Text>
																															</div>
																														</div>
																													),
																												}))}
																											/>
																										);
																									} catch (e) {
																										return (
																											<pre
																												style={{
																													fontSize: "12px",
																													whiteSpace: "pre-wrap",
																													backgroundColor: "#f5f5f5",
																													padding: "12px",
																													borderRadius: "4px",
																												}}>
																												{record.logs}
																											</pre>
																										);
																									}
																								})()}
																							</div>
																						),
																					});
																				}}></Button>
																		)}
																	</Space>
																),
															},
														]}
													/>
												) : (
													<div style={{ textAlign: "center", padding: "40px 0" }}>
														<PiCloudArrowUpDuotone size={48} style={{ color: "#d9d9d9" }} />
														<div style={{ marginTop: 16 }}>
															<Text type="secondary">No deployments found</Text>
														</div>
													</div>
												)}
											</Card>
										),
									},
								instance &&
									instance.type === "APLA" && {
										key: "logs",
										label: (
											<Space>
												<PiFileTextDuotone />
												Logs
											</Space>
										),
										children: (
											<Card
												title="APLA Logs"
												type="inner"
												style={{ marginBottom: 24 }}
												extra={
													<Button
														type="default"
														icon={<PiArrowDownDuotone />}
														onClick={() =>
															aplaLogsRef.current?.scrollTo({
																top: aplaLogsRef.current.scrollHeight,
																behavior: "smooth",
															})
														}>
														Scroll to Bottom
													</Button>
												}>
												<div ref={aplaLogsRef} style={{ maxHeight: "400px", overflowY: "auto" }}>
													<pre
														style={{
															margin: 0,
															fontSize: "12px",
															whiteSpace: "pre-wrap",
															backgroundColor: "#f5f5f5",
															padding: "16px",
															borderRadius: "6px",
														}}>
														{aplaLogs && aplaLogs.length > 0 ? (
															<>
																{aplaLogs.split("\n").map((line, index) => (
																	<div key={index}>&gt; {line}</div>
																))}
															</>
														) : (
															<div>Loading logs...</div>
														)}
													</pre>
												</div>
											</Card>
										),
									},
								{
									key: "settings",
									label: (
										<Space>
											<PiGearDuotone />
											<span>Settings</span>
										</Space>
									),
									children: (
										<Tabs
											type="line"
											size="small"
											tabPosition="left"
											className="no-margin-tabs"
											items={[
												{
													key: "general",
													label: (
														<Space>
															<span>General</span>
														</Space>
													),
													// instance have name and description, it can be edited here
													children: instance ? (
														<Card
															title="General Settings"
															type="inner"
															style={{ marginBottom: 24 }}>
															<Form
																layout="vertical"
																form={generalInfoForm}
																onFinish={handleUpdateInstance}>
																<Form.Item
																	label="Name"
																	name="name"
																	rules={[{ required: true, message: "Please enter a name" }]}>
																	<Input />
																</Form.Item>
																<Form.Item
																	label="Description"
																	name="description"
																	rules={[
																		{ required: true, message: "Please enter a description" },
																	]}>
																	<Input.TextArea rows={4} />
																</Form.Item>
																<Form.Item>
																	<Button type="primary" htmlType="submit">
																		Save
																	</Button>
																</Form.Item>
															</Form>
														</Card>
													) : null,
												},
												instance &&
													instance.type === "APLA" && {
														key: "environments",
														label: <Space>Environment Variables</Space>,
														children: (
															<Card
																title="Environments"
																type="inner"
																style={{ marginBottom: 24 }}
																extra={
																	<Button
																		type="default"
																		icon={<PiPlusDuotone />}
																		onClick={handleCreateEnvironment}>
																		Add Environment
																	</Button>
																}>
																{aplaEnvironments && aplaEnvironments.length > 0 ? (
																	<Space direction="vertical" style={{ width: "100%" }}>
																		<Divider orientation="left" orientationMargin={0} plain>
																			<strong>Production Environments</strong>
																		</Divider>
																		{aplaEnvironments
																			.filter((env) => env.is_preview === false)
																			.map((env) => (
																				<div
																					key={env.uuid}
																					style={{
																						width: "100%",
																						display: "flex",
																						alignItems: "center",
																						gap: "8px",
																					}}>
																					<div style={{ minWidth: "200px" }}>
																						<Input
																							value={env.key}
																							style={{ width: "100%" }}
																							onChange={(e) =>
																								handleEnvironmentChange(
																									env.uuid,
																									"key",
																									e.target.value
																								)
																							}
																						/>
																					</div>
																					<div style={{ flex: 1 }}>
																						<Input
																							value={env.value}
																							style={{ width: "100%" }}
																							onChange={(e) =>
																								handleEnvironmentChange(
																									env.uuid,
																									"value",
																									e.target.value
																								)
																							}
																						/>
																					</div>
																					<Popconfirm
																						title="Are you sure to delete this environment?"
																						onConfirm={() =>
																							handleDeleteEnvironmentSubmit(env.uuid)
																						}>
																						<Button type="text" danger icon={<PiTrashDuotone />} />
																					</Popconfirm>
																				</div>
																			))}
																		<Divider orientation="left" orientationMargin={0} plain>
																			<strong>Preview Environments</strong>
																		</Divider>
																		{aplaEnvironments
																			.filter((env) => env.is_preview === true)
																			.map((env) => (
																				<div
																					key={env.uuid}
																					style={{
																						width: "100%",
																						display: "flex",
																						alignItems: "center",
																						gap: "8px",
																					}}>
																					<div style={{ minWidth: "200px" }}>
																						<Input
																							value={env.key}
																							style={{ width: "100%" }}
																							onChange={(e) =>
																								handleEnvironmentChange(
																									env.uuid,
																									"key",
																									e.target.value
																								)
																							}
																						/>
																					</div>
																					<div style={{ flex: 1 }}>
																						<Input
																							value={env.value}
																							style={{ width: "100%" }}
																							onChange={(e) =>
																								handleEnvironmentChange(
																									env.uuid,
																									"value",
																									e.target.value
																								)
																							}
																						/>
																					</div>
																					<Popconfirm
																						title="Are you sure to delete this environment?"
																						onConfirm={() =>
																							handleDeleteEnvironmentSubmit(env.uuid)
																						}>
																						<Button type="text" danger icon={<PiTrashDuotone />} />
																					</Popconfirm>
																				</div>
																			))}
																		<Button
																			type="primary"
																			onClick={handleUpdateEnvironmentSubmit}
																			style={{ width: "100%", marginTop: 16 }}>
																			Save Environments
																		</Button>
																	</Space>
																) : (
																	<div style={{ textAlign: "center", padding: "20px 0" }}>
																		<Text type="secondary">No environments configured</Text>
																	</div>
																)}
															</Card>
														),
													},
												{
													key: "metadata",
													label: <Space>Metadata</Space>,
													children:
														instance.metadata && Object.keys(instance.metadata).length > 0 ? (
															<Card
																title="Metadata"
																type="inner"
																style={{ marginBottom: 24 }}
																extra={
																	<Button
																		type="default"
																		icon={<PiPencilDuotone />}
																		onClick={handleEditMetadata}>
																		Edit Metadata
																	</Button>
																}>
																<div
																	style={{
																		backgroundColor: "#f5f5f5",
																		padding: "16px",
																		borderRadius: "6px",
																	}}>
																	<pre
																		style={{ margin: 0, fontSize: "12px", wordWrap: "break-word" }}>
																		{JSON.stringify(instance.metadata, null, 2)}
																	</pre>
																</div>
															</Card>
														) : (
															<Card style={{ textAlign: "center", padding: "40px 0" }}>
																<Text type="secondary">No metadata available</Text>
																<br />
																<Button
																	type="primary"
																	icon={<PiPencilDuotone />}
																	onClick={handleEditMetadata}
																	style={{ marginTop: 16 }}>
																	Add Metadata
																</Button>
															</Card>
														),
												},
											]}
										/>
									),
								},
							]}
						/>
					</Col>
				</Row>
			</div>

			{/* Update Status Modal */}
			<Modal
				title={
					<Space>
						<PiPencilDuotone />
						<span>Update Instance Status</span>
					</Space>
				}
				open={updateStatusModalVisible}
				onCancel={handleUpdateStatusCancel}
				footer={null}
				width={500}>
				<Form
					form={statusForm}
					layout="vertical"
					onFinish={handleUpdateStatusSubmit}
					style={{ marginTop: 16 }}>
					<Form.Item
						label="Status"
						name="status"
						rules={[{ required: true, message: "Please select a status" }]}>
						<Select placeholder="Select new status">
							<Select.Option value="PENDING">Pending</Select.Option>
							<Select.Option value="PROVISIONING">Provisioning</Select.Option>
							<Select.Option value="RUNNING">Running</Select.Option>
							<Select.Option value="SUSPENDED">Suspended</Select.Option>
							<Select.Option value="CANCELLED">Cancelled</Select.Option>
							<Select.Option value="TERMINATED">Terminated</Select.Option>
						</Select>
					</Form.Item>

					<Form.Item
						label="Reason (Optional)"
						name="reason"
						extra="Provide a reason for the status change">
						<Select
							placeholder="Select or type a reason"
							mode="tags"
							maxTagCount={1}
							options={[
								{ value: "Status updated by admin" },
								{ value: "Customer request" },
								{ value: "System maintenance" },
								{ value: "Payment issue" },
								{ value: "Technical issue" },
								{ value: "Service migration" },
							]}
						/>
					</Form.Item>

					<Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
						<Space style={{ width: "100%", justifyContent: "flex-end" }}>
							<Button onClick={handleUpdateStatusCancel}>Cancel</Button>
							<Button type="primary" htmlType="submit" loading={updateStatusLoading}>
								Update Status
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>

			{/* Update Billing Modal */}
			<Modal
				title={
					<Space>
						<PiCurrencyDollarDuotone />
						<span>Update Instance Billing Information</span>
					</Space>
				}
				open={updateBillingModalVisible}
				onCancel={handleUpdateBillingCancel}
				footer={null}
				width={600}>
				<Form
					form={billingForm}
					layout="vertical"
					onFinish={handleUpdateBillingSubmit}
					style={{ marginTop: 16 }}>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								label="Expired Date"
								name="expiredAt"
								extra="When the instance will expire">
								<DatePicker
									showTime
									format="DD/MM/YYYY HH:mm"
									placeholder="Select expired date"
									style={{ width: "100%" }}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								label="Next Billing Date"
								name="nextBilledAt"
								extra="When the next bill will be generated">
								<DatePicker
									showTime
									format="DD/MM/YYYY HH:mm"
									placeholder="Select next billing date"
									style={{ width: "100%" }}
								/>
							</Form.Item>
						</Col>
					</Row>

					<Divider plain>
						<strong>Billing Cycle</strong>
					</Divider>

					<Row gutter={16}>
						<Col span={12}>
							<Form.Item
								label="Cycle Amount"
								name={["cycle", "amount"]}
								extra="Billing cycle amount (e.g., 1, 3, 6)">
								<InputNumber min={1} placeholder="Enter amount" style={{ width: "100%" }} />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item
								label="Cycle Interval"
								name={["cycle", "interval"]}
								extra="Billing cycle interval">
								<Select placeholder="Select interval">
									<Select.Option value="DAY">Day(s)</Select.Option>
									<Select.Option value="WEEK">Week(s)</Select.Option>
									<Select.Option value="MONTH">Month(s)</Select.Option>
									<Select.Option value="YEAR">Year(s)</Select.Option>
								</Select>
							</Form.Item>
						</Col>
					</Row>

					<Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
						<Space style={{ width: "100%", justifyContent: "flex-end" }}>
							<Button onClick={handleUpdateBillingCancel}>Cancel</Button>
							<Button type="primary" htmlType="submit" loading={updateBillingLoading}>
								Update Billing
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>

			{/* Edit Metadata Modal */}
			<Modal
				title={
					<Space>
						<PiPencilDuotone />
						<span>Edit Instance Metadata</span>
					</Space>
				}
				open={editMetadataModalVisible}
				onCancel={handleEditMetadataCancel}
				width={800}
				footer={null}>
				<Form form={metadataForm} layout="vertical" onFinish={handleEditMetadataSubmit}>
					<Form.Item
						label="Metadata (JSON Format)"
						name="metadata"
						rules={[
							{
								required: true,
								message: "Please enter metadata",
							},
							{
								validator: (_, value) => {
									if (!value) return Promise.resolve();
									try {
										JSON.parse(value);
										return Promise.resolve();
									} catch (e) {
										return Promise.reject(new Error("Invalid JSON syntax"));
									}
								},
							},
						]}
						extra="Enter metadata in valid JSON format. Changes will be applied immediately.">
						<Input.TextArea
							rows={15}
							placeholder={`{
  "key1": "value1",
  "key2": "value2",
  "nested": {
    "property": "value"
  }
}`}
							style={{ fontFamily: "Monaco, Menlo, 'Ubuntu Mono', monospace", fontSize: "12px" }}
						/>
					</Form.Item>

					<Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
						<Space style={{ width: "100%", justifyContent: "flex-end" }}>
							<Button onClick={handleEditMetadataCancel}>Cancel</Button>
							<Button type="primary" htmlType="submit" loading={editMetadataLoading}>
								Save Metadata
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>

			{/* Create Environment Modal */}
			<Modal
				title={
					<Space>
						<PiPencilDuotone />
						<span>Add New Environment Variable</span>
					</Space>
				}
				open={createEnvironmentModalVisible}
				onCancel={handleCreateEnvironmentCancel}
				width={512}
				footer={null}>
				<Form
					form={createEnvironmentForm}
					layout="vertical"
					onFinish={handleCreateEnvironmentSubmit}>
					<Form.Item
						label="Key"
						name="key"
						rules={[{ required: true, message: "Please enter environment key" }]}>
						<Input placeholder="Enter environment key" />
					</Form.Item>
					<Form.Item
						label="Value"
						name="value"
						rules={[{ required: true, message: "Please enter environment value" }]}>
						<Input.TextArea rows={4} placeholder="Enter environment value" />
					</Form.Item>
					<Form.Item name="isPreview" valuePropName="checked" style={{ marginBottom: 0 }}>
						<Checkbox>Set as preview environment only</Checkbox>
					</Form.Item>

					<Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
						<Space style={{ width: "100%", justifyContent: "flex-end" }}>
							<Button onClick={handleCreateEnvironmentCancel}>Cancel</Button>
							<Button type="primary" htmlType="submit" loading={createEnvironmentLoading}>
								Submit
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Modal>
		</div>
	);
}

export default InstanceViewPage;
